import React from "react";

// Background artwork for the /signup page. Everything is inline SVG so it
// stays sharp at any size and adds nothing to the page's network weight.

// Independence Arch with a trotro in front, drawn tone-on-tone over the green
// brand panel. Fits inside its box and sits on the bottom edge (xMidYMax meet),
// so callers control the size and where the "ground" is through the box.
export function MonumentSilhouette({ className = "" }) {
  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
    <svg
      className="w-full h-full"
      viewBox="0 360 600 440"
      preserveAspectRatio="xMidYMax meet"
    >
      <g fill="#000" fillOpacity="0.14">
        {/* Black star */}
        <path d="M300 374 L308.2 398.7 L334.2 398.9 L313.3 414.3 L321.2 439.1 L300 424 L278.8 439.1 L286.7 414.3 L265.8 398.9 L291.8 398.7 Z" />
        {/* Cornice */}
        <rect x="110" y="450" width="380" height="20" />
        <rect x="70" y="470" width="460" height="30" />
        {/* Arch body with its three openings cut out */}
        <path
          fillRule="evenodd"
          d="M90 500 H510 V760 H90 Z
             M240 760 V600 A60 60 0 0 1 360 600 V760 Z
             M130 760 V640 A35 35 0 0 1 200 640 V760 Z
             M400 760 V640 A35 35 0 0 1 470 640 V760 Z"
        />
        {/* Steps */}
        <rect x="50" y="760" width="500" height="16" />
        <rect x="20" y="776" width="560" height="24" />
      </g>

      {/* Trotro, a shade darker so it reads in front of the arch */}
      <g fill="#000" fillOpacity="0.2">
        <path
          fillRule="evenodd"
          d="M195 770 V712 Q195 694 213 694 H392 Q404 694 410 704 L428 738 Q432 745 432 752 V770 Z
             M210 704 H250 V728 H210 Z
             M258 704 H298 V728 H258 Z
             M306 704 H346 V728 H306 Z
             M354 704 H388 V728 H354 Z
             M396 704 H403 L419 732 H396 Z"
        />
        <circle cx="240" cy="772" r="14" />
        <circle cx="390" cy="772" r="14" />
      </g>
    </svg>
    </div>
  );
}

// Adinkra-style symbols, each drawn in a 100x100 box
const symbols = {
  adinkrahene: (
    <>
      <circle cx="50" cy="50" r="40" />
      <circle cx="50" cy="50" r="27" />
      <circle cx="50" cy="50" r="14" />
    </>
  ),
  sankofaHeart: (
    <>
      <path d="M50 88 C20 68 8 48 14 32 C20 16 42 14 50 30 C58 14 80 16 86 32 C92 48 80 68 50 88 Z" />
      <path d="M50 30 C46 44 34 44 34 36 C34 30 42 30 42 36" />
      <path d="M50 30 C54 44 66 44 66 36 C66 30 58 30 58 36" />
    </>
  ),
  ramsHorns: (
    <>
      <path d="M50 22 V78" />
      <path d="M50 40 C40 22 16 22 16 40 C16 52 32 52 32 42 C32 36 26 36 26 40" />
      <path d="M50 40 C60 22 84 22 84 40 C84 52 68 52 68 42 C68 36 74 36 74 40" />
      <path d="M50 60 C40 78 16 78 16 60 C16 48 32 48 32 58 C32 64 26 64 26 60" />
      <path d="M50 60 C60 78 84 78 84 60 C84 48 68 48 68 58 C68 64 74 64 74 60" />
    </>
  ),
  sankofaBird: (
    <>
      <path d="M20 70 C30 50 60 48 78 60 L90 56 L82 66 C70 80 40 82 20 70 Z" />
      <path d="M70 58 C74 40 64 26 50 28 C40 30 40 42 48 44 L40 48" />
      <circle cx="35" cy="50" r="4" />
      <path d="M45 79 V92 M58 79 V92" />
    </>
  ),
  knot: (
    <>
      <path d="M50 38 L62 50 L50 62 L38 50 Z" />
      <path d="M50 18 C62 18 62 38 50 38 C38 38 38 18 50 18 Z" />
      <path d="M50 62 C62 62 62 82 50 82 C38 82 38 62 50 62 Z" />
      <path d="M38 50 C38 38 18 38 18 50 C18 62 38 62 38 50 Z" />
      <path d="M62 50 C62 38 82 38 82 50 C82 62 62 62 62 50 Z" />
    </>
  ),
  spiral: (
    <path d="M50 50 C54 46 58 52 54 56 C48 62 40 54 44 46 C50 36 64 42 64 54 C64 68 44 72 36 60 C26 44 40 26 58 30 C76 34 82 56 72 70" />
  ),
};

// Where each symbol sits in one 400x400 pattern tile
const placements = [
  { symbol: "adinkrahene", x: 30, y: 30, scale: 0.6, rotate: 0 },
  { symbol: "sankofaHeart", x: 230, y: 60, scale: 0.55, rotate: -12 },
  { symbol: "ramsHorns", x: 110, y: 200, scale: 0.6, rotate: 8 },
  { symbol: "sankofaBird", x: 280, y: 230, scale: 0.6, rotate: 0 },
  { symbol: "knot", x: 40, y: 320, scale: 0.45, rotate: 20 },
  { symbol: "spiral", x: 190, y: 330, scale: 0.45, rotate: 0 },
  { symbol: "adinkrahene", x: 345, y: 20, scale: 0.35, rotate: 0 },
];

// Faint Adinkra symbols tiled over the light panel, with a flowing line-art
// ribbon in the bottom-right corner
export function AdinkraBackground({ className = "" }) {
  const ribbonLines = Array.from({ length: 16 }, (_, i) => (
    <path
      key={i}
      d={`M0 ${330 - i * 2} C180 ${250 - i * 6} 330 ${380 - i * 10} 600 ${190 - i * 8}`}
    />
  ));

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <pattern
            id="signup-adinkra"
            width="400"
            height="400"
            patternUnits="userSpaceOnUse"
          >
            <g
              fill="none"
              stroke="#9CABB8"
              strokeOpacity="0.35"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {placements.map((p, i) => (
                <g
                  key={i}
                  // Stroke is set per symbol so it lands at ~1.5px after scaling
                  strokeWidth={1.5 / p.scale}
                  transform={`translate(${p.x} ${p.y}) scale(${p.scale}) rotate(${p.rotate} 50 50)`}
                >
                  {symbols[p.symbol]}
                </g>
              ))}
            </g>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#signup-adinkra)" />
      </svg>

      <svg
        className="absolute bottom-0 right-0 w-full max-w-2xl"
        viewBox="0 0 600 360"
        preserveAspectRatio="xMaxYMax meet"
      >
        <defs>
          <linearGradient
            id="signup-ribbon-fade"
            x1="0"
            y1="0"
            x2="600"
            y2="0"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#12724A" stopOpacity="0" />
            <stop offset="0.5" stopColor="#12724A" stopOpacity="0.25" />
            <stop offset="1" stopColor="#12724A" stopOpacity="0.45" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="url(#signup-ribbon-fade)" strokeWidth="1">
          {ribbonLines}
        </g>
      </svg>
    </div>
  );
}
